"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const ImageWorker_1 = require("./imageWorker/ImageWorker");
/**
 * Platform-specific functionality.
 * Copyright Metrological, 2017
 * Copyright Bas van Meurs, 2020
 */
class WebPlatform {
    constructor(stage) {
        this.stage = stage;
        this._looping = false;
        this._awaitingLoop = false;
        this._imageWorker = undefined;
    }
    init() {
        if (this.stage.useImageWorker) {
            if (!window.createImageBitmap || !window.Worker) {
                console.warn("Can't use image worker because browser does not have createImageBitmap and Web Worker support");
            }
            else {
                // Firefox does support createImageBitmap, but not with the required paramater signature.
                const canvas = this.stage.getDrawingCanvas();
                canvas.width = 1;
                canvas.height = 1;
                window.createImageBitmap(canvas, {
                    premultiplyAlpha: "premultiply",
                    colorSpaceConversion: "none",
                    imageOrientation: "none",
                })
                    .then(() => {
                    console.log("Using image worker!");
                    this._imageWorker = new ImageWorker_1.ImageWorker();
                })
                    .catch(() => {
                    console.warn("Can't use image worker: createImageBitmap does not support signature. Using on-thread image parsing.");
                });
            }
        }
    }
    destroy() {
        if (this._imageWorker) {
            this._imageWorker.destroy();
        }
    }
    startLoop() {
        this._looping = true;
        if (!this._awaitingLoop) {
            this.loop();
        }
    }
    stopLoop() {
        this._looping = false;
    }
    loop() {
        const lp = () => {
            this._awaitingLoop = false;
            if (this._looping) {
                this.stage.drawFrame();
                requestAnimationFrame(lp);
                this._awaitingLoop = true;
            }
        };
        requestAnimationFrame(lp);
    }
    uploadGlTexture(gl, textureSource, source, options) {
        if (source.buffer) {
            // Uint8Array
            gl.texImage2D(gl.TEXTURE_2D, 0, options.internalFormat, textureSource.w, textureSource.h, 0, options.format, options.type, source);
        }
        else {
            // Web-specific data types.
            gl.texImage2D(gl.TEXTURE_2D, 0, options.internalFormat, options.format, options.type, source);
        }
    }
    loadSrcTexture(info, cb) {
        const { src, hasAlpha } = info;
        let cancelCb;
        const isPng = src.indexOf(".png") >= 0;
        if (this._imageWorker) {
            // WPE-specific image parser.
            const image = this._imageWorker.create(src);
            image.onError = (err) => {
                return cb(err);
            };
            image.onLoad = (imageInfo) => {
                cb(undefined, {
                    source: imageInfo.imageBitmap,
                    renderInfo: { src },
                    hasAlpha: imageInfo.hasAlphaChannel,
                    premultiplyAlpha: true,
                });
            };
            cancelCb = () => {
                image.cancel();
            };
        }
        else {
            const image = new Image();
            if (!(src.substr(0, 5) === "data:")) {
                // Base64.
                image.crossOrigin = "Anonymous";
            }
            image.onerror = (err) => {
                // Ignore error message when cancelled.
                if (image.src) {
                    return cb(new Error("Image loading error: " + err));
                }
            };
            image.onload = () => {
                cb(undefined, {
                    source: image,
                    renderInfo: { src },
                    hasAlpha: isPng || hasAlpha,
                });
            };
            image.src = src;
            cancelCb = () => {
                image.onerror = null;
                image.onload = null;
                image.removeAttribute("src");
            };
        }
        return cancelCb;
    }
    createWebGLContext() {
        const canvas = this.stage.canvas;
        const opts = {
            alpha: true,
            antialias: false,
            premultipliedAlpha: true,
            stencil: true,
            preserveDrawingBuffer: false,
        };
        const gl = (canvas.getContext("webgl", opts) ||
            canvas.getContext("experimental-webgl", opts));
        if (!gl) {
            throw new Error("This browser does not support webGL.");
        }
        return gl;
    }
    createCanvasContext() {
        const canvas = this.stage.canvas;
        const c2d = canvas.getContext("2d");
        if (!c2d) {
            throw new Error("This browser does not support 2d canvas.");
        }
        return c2d;
    }
    getHrTime() {
        return window.performance ? window.performance.now() : new Date().getTime();
    }
    getDrawingCanvas() {
        // We can't reuse this canvas because textures may load async.
        return document.createElement("canvas");
    }
    getTextureOptionsForDrawingCanvas(canvas) {
        const options = {
            source: canvas,
        };
        return options;
    }
    nextFrame(changes) {
        /* WebGL blits automatically */
    }
}
exports.WebPlatform = WebPlatform;
//# sourceMappingURL=WebPlatform.js.map